import { Suspense, lazy } from 'react';
import { useRecoveryLocation } from '@/hooks/useRecoveryLocation';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { SentryTestPage } from './pages/SentryTestPage';

// Admin panel (platform admin — completely separate from tenant routes, NOT part of the v1→v2 cutover)
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminRouteGuard } from './components/admin/AdminRouteGuard';

// Admin sub-pages (lazy)
const AdminTenantsPage = lazy(() => import('./pages/admin/tenants/AdminTenantsPage').then(m => ({ default: m.AdminTenantsPage })));
const AdminTenantDetailPage = lazy(() => import('./pages/admin/tenants/AdminTenantDetailPage').then(m => ({ default: m.AdminTenantDetailPage })));
const AdminUsersPage = lazy(() => import('./pages/admin/users/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminPlatformAdminsPage = lazy(() => import('./pages/admin/platform-admins/AdminPlatformAdminsPage').then(m => ({ default: m.AdminPlatformAdminsPage })));
const AdminWhatsAppPage = lazy(() => import('./pages/admin/whatsapp/AdminWhatsAppPage').then(m => ({ default: m.AdminWhatsAppPage })));
const AdminWebhooksPage = lazy(() => import('./pages/admin/webhooks/AdminWebhooksPage').then(m => ({ default: m.AdminWebhooksPage })));
const AdminLgpdPage = lazy(() => import('./pages/admin/lgpd/AdminLgpdPage').then(m => ({ default: m.AdminLgpdPage })));
const AdminAuditPage = lazy(() => import('./pages/admin/audit/AdminAuditPage').then(m => ({ default: m.AdminAuditPage })));
const AdminNpsPage = lazy(() => import('./pages/admin/nps/AdminNpsPage').then(m => ({ default: m.AdminNpsPage })));
const AdminEmailsPage = lazy(() => import('./pages/admin/emails/AdminEmailsPage').then(m => ({ default: m.AdminEmailsPage })));

// Public standalone pages (no auth, no layout)
import { NpsRespondPage } from './pages/NpsRespondPage';
import { V2Routes } from './v2/routes';
const DocumentSigningPage = lazy(() =>
  import('./pages/DocumentSigningPage').then((module) => ({ default: module.DocumentSigningPage })),
);

// Loading fallback
function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center" role="status" aria-label="Carregando página">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
    </div>
  );
}

/** Redirect preserving route params (:id) and the query string (?token). */
function Redirect({ to }: { to: string }) {
  const loc = useLocation();
  const params = useParams();
  let target = to;
  for (const [k, v] of Object.entries(params)) {
    if (v) target = target.replace(`:${k}`, v);
  }

  const [path, targetSearch = ''] = target.split('?');
  const mergedSearch = new URLSearchParams(targetSearch);
  const currentSearch = new URLSearchParams(loc.search);
  currentSearch.forEach((value, key) => {
    if (!mergedSearch.has(key)) mergedSearch.append(key, value);
  });
  const query = mergedSearch.toString();

  return <Navigate to={`${path}${query ? `?${query}` : ''}`} replace />;
}

function StripLegacyPrefixRedirect({ prefix }: { prefix: '/v1' | '/v2' }) {
  const loc = useLocation();
  const stripped = loc.pathname.replace(new RegExp(`^${prefix}/?`), '/');
  const path = stripped === '/' ? '/dashboard' : stripped;
  return <Navigate to={`${path}${loc.search}`} replace />;
}

const LEGACY_CANONICAL_ALIASES: [string, string][] = [
  ['/pacientes/novo', '/pacientes?new=1'],
  ['/campanhas/nova', '/campanhas?new=1'],
  ['/configuracoes/whatsapp', '/config/whatsapp'],
  ['/config/quick-replies', '/config/respostas'],
  ['/config/message-templates', '/config/mensagens'],
  ['/onboarding', '/primeiros-passos'],
];

function App() {
  const location = useRecoveryLocation();
  return (
    <Routes location={location}>
      {/* Public standalone routes — no auth, no layout */}
      <Route path="/nps/respond/:token" element={<NpsRespondPage />} />
      <Route
        path="/assinar/:token"
        element={
          <Suspense fallback={<PageLoader />}>
            <DocumentSigningPage />
          </Suspense>
        }
      />
      {import.meta.env.DEV && <Route path="/sentry-test" element={<SentryTestPage />} />}

      {/* Legacy prefixes are retired. Keep URL compatibility, but promote to canonical root paths. */}
      <Route path="/v2/*" element={<StripLegacyPrefixRedirect prefix="/v2" />} />
      <Route path="/v1/*" element={<StripLegacyPrefixRedirect prefix="/v1" />} />

      {/* Canonical aliases that express an intent, not a real route. */}
      {LEGACY_CANONICAL_ALIASES.map(([from, to]) => (
        <Route key={from} path={from} element={<Redirect to={to} />} />
      ))}

      {/* Admin panel routes — completely separate from tenant routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route
        path="/admin"
        element={
          <AdminRouteGuard>
            <AdminLayout />
          </AdminRouteGuard>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="tenants" element={<Suspense fallback={<PageLoader />}><AdminTenantsPage /></Suspense>} />
        <Route path="tenants/:id" element={<Suspense fallback={<PageLoader />}><AdminTenantDetailPage /></Suspense>} />
        <Route path="users" element={<Suspense fallback={<PageLoader />}><AdminUsersPage /></Suspense>} />
        <Route path="platform-admins" element={<Suspense fallback={<PageLoader />}><AdminPlatformAdminsPage /></Suspense>} />
        <Route path="whatsapp" element={<Suspense fallback={<PageLoader />}><AdminWhatsAppPage /></Suspense>} />
        <Route path="emails" element={<Suspense fallback={<PageLoader />}><AdminEmailsPage /></Suspense>} />
        <Route path="webhooks" element={<Suspense fallback={<PageLoader />}><AdminWebhooksPage /></Suspense>} />
        <Route path="lgpd" element={<Suspense fallback={<PageLoader />}><AdminLgpdPage /></Suspense>} />
        <Route path="audit" element={<Suspense fallback={<PageLoader />}><AdminAuditPage /></Suspense>} />
        <Route path="nps" element={<Suspense fallback={<PageLoader />}><AdminNpsPage /></Suspense>} />
      </Route>

      {/* Sober DS v2 is now the canonical application, mounted at original paths. */}
      <Route path="/*" element={<V2Routes />} />
    </Routes>
  );
}

export default App;
